<template>
  <div class="tiptap-editor border border-gray-300 rounded-md overflow-hidden">
    <!-- Toolbar wrapper -->
    <div class="relative">
    <!-- Toolbar -->
    <div class="tiptap-toolbar flex flex-wrap gap-1 p-2 border-b border-gray-300 bg-gray-50"
         role="group" :aria-label="$t('products.editor_toolbar')">
      <!-- Text style -->
      <button type="button" @click="editor.chain().focus().toggleBold().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('bold') }"
              :aria-pressed="(editor && editor.isActive('bold')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_bold')" :aria-label="$t('products.editor_bold')">
        <strong>B</strong>
      </button>
      <button type="button" @click="editor.chain().focus().toggleItalic().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('italic') }"
              :aria-pressed="(editor && editor.isActive('italic')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_italic')" :aria-label="$t('products.editor_italic')">
        <em>I</em>
      </button>
      <button type="button" @click="editor.chain().focus().toggleUnderline().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('underline') }"
              :aria-pressed="(editor && editor.isActive('underline')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_underline')" :aria-label="$t('products.editor_underline')">
        <u>U</u>
      </button>
      <button type="button" @click="editor.chain().focus().toggleStrike().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('strike') }"
              :aria-pressed="(editor && editor.isActive('strike')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_strike')" :aria-label="$t('products.editor_strike')">
        <s>S</s>
      </button>

      <span class="toolbar-divider"></span>

      <!-- Headings -->
      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('heading', { level: 2 }) }"
              :aria-pressed="(editor && editor.isActive('heading', { level: 2 })) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_heading_2')" :aria-label="$t('products.editor_heading_2')">
        H2
      </button>
      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('heading', { level: 3 }) }"
              :aria-pressed="(editor && editor.isActive('heading', { level: 3 })) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_heading_3')" :aria-label="$t('products.editor_heading_3')">
        H3
      </button>
      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('heading', { level: 4 }) }"
              :aria-pressed="(editor && editor.isActive('heading', { level: 4 })) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_heading_4')" :aria-label="$t('products.editor_heading_4')">
        H4
      </button>

      <span class="toolbar-divider"></span>

      <!-- Lists -->
      <button type="button" @click="editor.chain().focus().toggleBulletList().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('bulletList') }"
              :aria-pressed="(editor && editor.isActive('bulletList')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_bullet_list')" :aria-label="$t('products.editor_bullet_list')">
        &#8226;&#8212;
      </button>
      <button type="button" @click="editor.chain().focus().toggleOrderedList().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('orderedList') }"
              :aria-pressed="(editor && editor.isActive('orderedList')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_ordered_list')" :aria-label="$t('products.editor_ordered_list')">
        1&#8212;
      </button>

      <span class="toolbar-divider"></span>

      <!-- Alignment -->
      <button type="button" @click="editor.chain().focus().setTextAlign('left').run()"
              :class="{ 'bg-gray-200': editor && editor.isActive({ textAlign: 'left' }) }"
              :aria-pressed="(editor && editor.isActive({ textAlign: 'left' })) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_align_left')" :aria-label="$t('products.editor_align_left')">
        &#8676;
      </button>
      <button type="button" @click="editor.chain().focus().setTextAlign('center').run()"
              :class="{ 'bg-gray-200': editor && editor.isActive({ textAlign: 'center' }) }"
              :aria-pressed="(editor && editor.isActive({ textAlign: 'center' })) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_align_center')" :aria-label="$t('products.editor_align_center')">
        &#8284;
      </button>
      <button type="button" @click="editor.chain().focus().setTextAlign('right').run()"
              :class="{ 'bg-gray-200': editor && editor.isActive({ textAlign: 'right' }) }"
              :aria-pressed="(editor && editor.isActive({ textAlign: 'right' })) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_align_right')" :aria-label="$t('products.editor_align_right')">
        &#8677;
      </button>

      <span class="toolbar-divider"></span>

      <!-- Blockquote & Code -->
      <button type="button" @click="editor.chain().focus().toggleBlockquote().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('blockquote') }"
              :aria-pressed="(editor && editor.isActive('blockquote')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_blockquote')" :aria-label="$t('products.editor_blockquote')">
        &#10077;
      </button>
      <button type="button" @click="editor.chain().focus().toggleCodeBlock().run()"
              :class="{ 'bg-gray-200': editor && editor.isActive('codeBlock') }"
              :aria-pressed="(editor && editor.isActive('codeBlock')) ? 'true' : 'false'"
              class="toolbar-btn font-mono" :title="$t('products.editor_code_block')" :aria-label="$t('products.editor_code_block')">
        &lt;/&gt;
      </button>

      <span class="toolbar-divider"></span>

      <!-- Link -->
      <button type="button" @click="toggleLinkInput"
              :class="{ 'bg-gray-200': editor && editor.isActive('link') }"
              :aria-pressed="(editor && editor.isActive('link')) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_link')" :aria-label="$t('products.editor_link')">
        &#128279;
      </button>
      <button type="button" @click="editor.chain().focus().unsetLink().run()"
              :disabled="editor && !editor.isActive('link')"
              class="toolbar-btn" :title="$t('products.editor_remove_link')" :aria-label="$t('products.editor_remove_link')">
        &#128280;
      </button>

      <span class="toolbar-divider"></span>

      <!-- Image -->
      <button type="button" @click="toggleImageInput"
              class="toolbar-btn" :title="$t('products.editor_insert_image')" :aria-label="$t('products.editor_insert_image')">
        &#128247;
      </button>

      <span class="toolbar-divider"></span>

      <!-- Horizontal rule (with `blocks` it lives in the "Blocks" menu) -->
      <template v-if="!blocks">
        <button type="button" @click="editor.chain().focus().setHorizontalRule().run()"
                class="toolbar-btn" :title="$t('products.editor_horizontal_rule')" :aria-label="$t('products.editor_horizontal_rule')">
          &#8212;
        </button>

        <span class="toolbar-divider"></span>
      </template>

      <!-- Text color -->
      <label class="toolbar-btn" :title="$t('products.editor_text_color')" style="cursor:pointer">
        <span :style="{ borderBottom: '2px solid ' + currentColor, paddingBottom: '1px' }" aria-hidden="true">A</span>
        <input ref="colorInput" type="color" class="sr-only" :value="currentColor"
               @input="setTextColor($event.target.value)" :aria-label="$t('products.editor_text_color')" />
      </label>
      <button type="button" @click="editor.chain().focus().unsetColor().run()"
              class="toolbar-btn text-xs" :title="$t('products.editor_remove_color')" :aria-label="$t('products.editor_remove_color')">
        A&#10006;
      </button>

      <span class="toolbar-divider"></span>

      <!-- Undo / Redo -->
      <button type="button" @click="editor.chain().focus().undo().run()"
              :disabled="editor && !editor.can().undo()"
              class="toolbar-btn" :title="$t('products.editor_undo')" :aria-label="$t('products.editor_undo')">
        &#8617;
      </button>
      <button type="button" @click="editor.chain().focus().redo().run()"
              :disabled="editor && !editor.can().redo()"
              class="toolbar-btn" :title="$t('products.editor_redo')" :aria-label="$t('products.editor_redo')">
        &#8618;
      </button>

      <span class="toolbar-divider"></span>

      <!-- HTML source toggle -->
      <button type="button" @click="toggleHtmlMode"
              :class="{ 'bg-gray-200': showHtmlSource }"
              :aria-pressed="(showHtmlSource) ? 'true' : 'false'"
              class="toolbar-btn" :title="$t('products.editor_edit_html')" :aria-label="$t('products.editor_edit_html')">
        <font-awesome-icon icon="file-code" />
      </button>

      <span class="toolbar-divider"></span>

      <!-- Highlight badge & icon list (with `blocks` they live in the "Blocks" menu) -->
      <template v-if="!blocks">
        <button type="button" @click="editor.chain().focus().insertHighlightBadge().run()"
                :class="{ 'bg-gray-200': editor && editor.isActive('highlightBadge') }"
              :aria-pressed="(editor && editor.isActive('highlightBadge')) ? 'true' : 'false'"
                class="toolbar-btn" :title="$t('products.editor_insert_badge')" :aria-label="$t('products.editor_insert_badge')">
          <font-awesome-icon icon="tag" />
        </button>

        <button type="button" @click="editor.chain().focus().insertIconList().run()"
                :class="{ 'bg-gray-200': editor && (editor.isActive('iconList') || editor.isActive('iconListItem')) }"
              :aria-pressed="(editor && (editor.isActive('iconList') || editor.isActive('iconListItem'))) ? 'true' : 'false'"
                class="toolbar-btn" :title="$t('products.editor_insert_icon_list')" :aria-label="$t('products.editor_insert_icon_list')">
          <font-awesome-icon icon="list-ul" />
        </button>

        <span class="toolbar-divider"></span>
      </template>

      <!-- Predesigned blocks menu: only where the parent enables `blocks` (product page editors) -->
      <template v-if="blocks">
        <span ref="blocksMenu" class="relative inline-flex"
              @keydown.escape="closeBlocksMenu({ focusTrigger: true })">
          <button ref="blocksMenuTrigger" type="button" @click="toggleBlocksMenu"
                  :class="{ 'bg-gray-200': showBlocksMenu }"
                  class="toolbar-btn gap-1 focus:outline-none focus:ring-2 focus:ring-bpblue"
                  :title="$t('products.blocks_menu_label')"
                  :aria-expanded="showBlocksMenu ? 'true' : 'false'"
                  :aria-controls="blocksMenuId">
            <font-awesome-icon icon="th-large" aria-hidden="true" />
            <span>{{ $t('products.blocks_menu') }}</span>
            <font-awesome-icon icon="chevron-down" class="text-xs" aria-hidden="true" />
          </button>

          <div v-if="showBlocksMenu" :id="blocksMenuId"
               class="absolute left-0 top-full z-20 mt-1 min-w-[10rem] py-1 bg-white border border-gray-200 rounded-md shadow-md">
            <button v-for="entry in blockMenuEntries" :key="entry.key" type="button"
                    class="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-bpblue"
                    @click="insertBlock(entry)">
              {{ $t(entry.label) }}
            </button>
          </div>
        </span>
      </template>

      <!-- Raw HTML: deliberately styled apart from the rest — unrestricted escape hatch.
           Not offered with `blocks`; existing raw HTML blocks stay viewable and editable. -->
      <button v-if="!blocks" type="button" @click="editor.chain().focus().insertRawHtml().run()"
              class="toolbar-btn toolbar-btn-danger" :title="$t('products.editor_insert_raw_html')" :aria-label="$t('products.editor_insert_raw_html')">
        <font-awesome-icon icon="code" />
      </button>
    </div>
    </div>
    <!-- /Toolbar wrapper (now wraps only the toolbar itself) -->

    <!-- Fixed-height gutter for the badge/icon-list color panels. Height is CONSTANT
         regardless of whether a panel is open, so toggling isBadgeSelected /
         isIconListSelected never resizes anything below it — that's what keeps an
         in-progress mouse-drag text selection inside the badge in sync (see history:
         the panel used to be in normal flow and pushed editor-content on open/close,
         desyncing the drag; making it absolute fixed that but left it overlapping
         editor-content instead, since nothing reserved its space — this gutter reserves
         it permanently). Panels fill the gutter via absolute + inset-0 instead of
         top-full off the toolbar, so they never paint over editor-content below. -->
    <div class="relative h-11">
      <!-- Inline highlight badge color panel. Mutually exclusive with the link/image
           bars and the icon list panel: they all occupy this same gutter. -->
      <div v-if="isBadgeSelected && !showLinkInput && !showImageInput && !isIconListSelected"
           class="absolute inset-0 z-10 flex items-center gap-4 p-2 border-b border-x border-gray-200 bg-white shadow-md">
        <span class="text-sm text-gray-600 whitespace-nowrap">{{ $t('products.editor_badge_panel') }}</span>
        <label class="flex items-center gap-1 text-xs text-gray-600">
          {{ $t('products.editor_badge_border') }}
          <input type="color" :value="badgeBorderColor" @input="setBadgeBorderColor($event.target.value)" />
        </label>
        <label class="flex items-center gap-1 text-xs text-gray-600">
          {{ $t('products.editor_badge_text') }}
          <input type="color" :value="badgeTextColor" @input="setBadgeTextColor($event.target.value)" />
        </label>
        <label class="flex items-center gap-1 text-xs text-gray-600">
          {{ $t('products.editor_badge_background') }}
          <input type="color" :value="badgeBackgroundColor" @input="setBadgeBackgroundColor($event.target.value)" />
        </label>
      </div>

      <!-- Inline icon list color panel: same treatment, same mutual exclusion. -->
      <div v-if="isIconListSelected && !showLinkInput && !showImageInput && !isBadgeSelected"
           class="absolute inset-0 z-10 flex items-center gap-4 p-2 border-b border-x border-gray-200 bg-white shadow-md">
        <span class="text-sm text-gray-600 whitespace-nowrap">{{ $t('products.editor_icon_list_panel') }}</span>
        <label class="flex items-center gap-1 text-xs text-gray-600">
          {{ $t('products.editor_icon_list_color') }}
          <input type="color" :value="iconListColor" @input="setIconListColor($event.target.value)" />
        </label>
      </div>
    </div>
    <!-- /Color panel gutter -->

    <!-- Inline link input -->
    <div v-if="showLinkInput" class="flex items-center gap-2 p-2 border-b border-gray-200 bg-white">
      <label for="tiptap-link-input" class="text-sm text-gray-600 whitespace-nowrap">{{ $t('products.editor_link_url') }}</label>
      <input id="tiptap-link-input" ref="linkInput" type="url" v-model="linkUrl"
             class="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-blue-400"
             placeholder="https://example.com"
             @keydown.enter.prevent="applyLink"
             @keydown.escape="cancelLinkInput" />
      <button type="button" @click="applyLink"
              class="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">
        {{ $t('products.editor_apply') }}
      </button>
      <button type="button" @click="cancelLinkInput" :title="$t('products.editor_cancel')" :aria-label="$t('products.editor_cancel')"
              class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
        <span aria-hidden="true">✕</span>
      </button>
    </div>

    <!-- Inline image input -->
    <div v-if="showImageInput" class="flex items-center gap-2 p-2 border-b border-gray-200 bg-white">
      <label for="tiptap-image-input" class="text-sm text-gray-600 whitespace-nowrap">{{ $t('products.editor_image_url') }}</label>
      <input id="tiptap-image-input" ref="imageInput" type="url" v-model="imageUrl"
             class="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-blue-400"
             placeholder="https://example.com/image.png"
             @keydown.enter.prevent="applyImage"
             @keydown.escape="cancelImageInput" />
      <button type="button" @click="applyImage"
              class="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">
        {{ $t('products.editor_apply') }}
      </button>
      <button type="button" @click="cancelImageInput" :title="$t('products.editor_cancel')" :aria-label="$t('products.editor_cancel')"
              class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
        <span aria-hidden="true">✕</span>
      </button>
    </div>

    <!-- HTML source editor -->
    <textarea v-if="showHtmlSource" v-model="htmlSource" :aria-label="$t('products.editor_html_source')"
              class="tiptap-html-source w-full p-4 font-mono text-sm border-0 focus:outline-none resize-y"
              style="min-height: 150px"
              spellcheck="false"
              @blur="applyHtmlSource"
    ></textarea>

    <!-- Editor area -->
    <editor-content v-show="!showHtmlSource" :editor="editor" class="tiptap-content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import HighlightBadgeNode from './nodes/HighlightBadgeNode'
import IconListNode from './nodes/IconListNode'
import IconListItemNode from './nodes/IconListItemNode'
import RawHtmlNode from './nodes/RawHtmlNode'
import RestrictedHeadingNode from './nodes/RestrictedHeadingNode'
import PriceEnrollNode from './nodes/PriceEnrollNode'
import InfoPricesNode from './nodes/InfoPricesNode'
import LinkButtonNode from './nodes/LinkButtonNode'
import { normalizeHexColor, tintHexColor, darkenToContrast } from './nodes/colorAttributes'

// Entries of the "Blocks" menu. Adding a block = adding an entry whose `command`
// exists on the editor; `label` is an i18n key. Optional `attrs(brandHex, t)` returns the
// attributes to insert with (`brandHex` is null when the academy has no valid brand color).
const BLOCK_MENU_ENTRIES = [
  {
    key: 'badge',
    label: 'products.block_badge',
    command: 'insertHighlightBadge',
    // Brand border on a light tint of it; the text is the brand color darkened just enough
    // to stay readable (>= 4.5:1) on that tint. Without brand color, the badge defaults.
    attrs: brand => {
      if (!brand) return undefined
      const background = tintHexColor(brand)
      return { borderColor: brand, textColor: darkenToContrast(brand, background), backgroundColor: background }
    },
  },
  { key: 'keyFacts', label: 'products.block_key_facts', command: 'insertIconList' },
  { key: 'separator', label: 'products.block_separator', command: 'setHorizontalRule' },
  {
    key: 'priceEnroll',
    label: 'products.block_price_enroll',
    command: 'insertPriceEnroll',
    attrs: (brand, t) => ({
      buttonText: t('products.price_enroll_default_button'),
      ...(brand && { buttonColor: brand }),
    }),
  },
  {
    key: 'infoPrices',
    label: 'products.block_info_prices',
    command: 'insertInfoPrices',
    attrs: (brand, t) => ({
      rows: [
        { label: t('products.info_prices_default_row_1'), amount: '00 €' },
        { label: t('products.info_prices_default_row_2'), amount: '00 €' },
      ],
      ...(brand && { accentColor: brand }),
    }),
  },
  {
    key: 'linkButton',
    label: 'products.block_link_button',
    command: 'insertLinkButton',
    attrs: (brand, t) => ({
      text: t('products.link_button_default_text'),
      ...(brand && { color: brand }),
    }),
  },
]

// Inline style for <hr> in `blocks` editors: the public product page has no `prose`, and Tailwind's
// reset leaves a bare <hr> with no margin.
const BLOCKS_HR_STYLE = 'border:0;border-top:1px solid #e5e7eb;margin:24px 0;'

export default {
  name: 'TipTapEditorComponent',

  components: {
    EditorContent,
  },

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    // Shows the predesigned "Blocks" menu (enabled by the product page editors).
    blocks: {
      type: Boolean,
      default: false,
    },
    // Academy brand color, used as the default color of blocks inserted from the "Blocks" menu.
    brandColor: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      editor: null,
      // True while our own update:modelValue emit is round-tripping back down as
      // a prop change, so the watcher below doesn't overwrite fresh keystrokes with a stale echo.
      isInternalUpdate: false,
      currentColor: '#000000',
      showLinkInput: false,
      linkUrl: '',
      showImageInput: false,
      imageUrl: '',
      showHtmlSource: false,
      htmlSource: '',
      isBadgeSelected: false,
      badgeBorderColor: '#045DB5',
      badgeTextColor: '#045DB5',
      badgeBackgroundColor: '#eaf1fb',
      isIconListSelected: false,
      iconListColor: '#1f2937',
      showBlocksMenu: false,
      blockMenuEntries: BLOCK_MENU_ENTRIES,
    }
  },

  computed: {
    // Unique per editor instance: the product page shows several editors with a "Blocks" menu.
    blocksMenuId() {
      return `tiptap-blocks-menu-${this.$.uid}`
    },
    // Brand color as #rrggbb, or null if it isn't a valid hex (blocks then keep their default colors).
    brandHex() {
      return normalizeHexColor(this.brandColor)
    },
  },

  mounted() {
    this.editor = new Editor({
      editorProps: {
        attributes: {
          class: 'prose prose-sm sm:prose lg:prose-lg max-w-none p-4 min-h-[150px] focus:outline-none',
        },
      },
      extensions: [
        // StarterKit's own heading is replaced by RestrictedHeadingNode (levels 2-4 only)
        StarterKit.configure({
          heading: false,
          horizontalRule: this.blocks ? { HTMLAttributes: { style: BLOCKS_HR_STYLE } } : {},
        }),
        RestrictedHeadingNode.configure({ levels: [2, 3, 4] }),
        Underline,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            rel: 'noopener noreferrer',
            target: '_blank',
          },
        }),
        Image,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        TextStyle,
        Color,
        HighlightBadgeNode,
        IconListNode,
        IconListItemNode,
        RawHtmlNode,
        // Registered everywhere so saved blocks are kept in any editor; only offered with `blocks`.
        PriceEnrollNode,
        InfoPricesNode,
        LinkButtonNode,
      ],
      content: this.modelValue,
      onUpdate: () => {
        this.isInternalUpdate = true
        this.$emit('update:modelValue', this.editor.getHTML())
        this.$nextTick(() => {
          this.isInternalUpdate = false
        })
      },
      onSelectionUpdate: () => {
        const color = this.editor.getAttributes('textStyle').color
        this.currentColor = color || '#000000'

        this.isBadgeSelected = this.editor.isActive('highlightBadge')
        if (this.isBadgeSelected) {
          const attrs = this.editor.getAttributes('highlightBadge')
          this.badgeBorderColor = attrs.borderColor
          this.badgeTextColor = attrs.textColor
          this.badgeBackgroundColor = attrs.backgroundColor
        }

        this.isIconListSelected = this.editor.isActive('iconList') || this.editor.isActive('iconListItem')
        if (this.isIconListSelected) {
          this.iconListColor = this.editor.getAttributes('iconList').iconColor
        }
      },
    })
  },

  watch: {
    modelValue(value) {
      if (!this.editor || this.isInternalUpdate) return
      const isSame = this.editor.getHTML() === value
      if (isSame) return
      this.editor.commands.setContent(value, { emitUpdate: false })
    },
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleBlocksMenuOutsideClick, true)
    if (this.editor) {
      this.editor.destroy()
    }
  },

  methods: {
    setTextColor(color) {
      this.currentColor = color
      this.editor.chain().focus().setColor(color).run()
    },

    toggleLinkInput() {
      this.showImageInput = false
      this.linkUrl = this.editor.getAttributes('link').href || ''
      this.showLinkInput = !this.showLinkInput
      if (this.showLinkInput) {
        this.$nextTick(() => this.$refs.linkInput && this.$refs.linkInput.focus())
      }
    },
    applyLink() {
      const { from, to } = this.editor.state.selection
      const hasSelection = from !== to
      if (!hasSelection && !this.editor.isActive('link')) {
        this.showLinkInput = false
        this.linkUrl = ''
        return
      }
      if (!this.linkUrl) {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
      } else {
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: this.linkUrl }).run()
      }
      this.showLinkInput = false
      this.linkUrl = ''
    },
    cancelLinkInput() {
      this.showLinkInput = false
      this.linkUrl = ''
    },

    toggleImageInput() {
      this.showLinkInput = false
      this.imageUrl = ''
      this.showImageInput = !this.showImageInput
      if (this.showImageInput) {
        this.$nextTick(() => this.$refs.imageInput && this.$refs.imageInput.focus())
      }
    },
    applyImage() {
      if (this.imageUrl && /^https?:\/\//i.test(this.imageUrl)) {
        this.editor.chain().focus().setImage({ src: this.imageUrl }).run()
      }
      this.showImageInput = false
      this.imageUrl = ''
    },
    cancelImageInput() {
      this.showImageInput = false
      this.imageUrl = ''
    },

    toggleHtmlMode() {
      if (!this.showHtmlSource) {
        // entering HTML mode: populate textarea with current editor HTML
        this.htmlSource = this.editor.getHTML()
        this.showLinkInput = false
        this.showImageInput = false
      } else {
        // leaving HTML mode: apply the edited HTML back to the editor
        this.applyHtmlSource()
      }
      this.showHtmlSource = !this.showHtmlSource
    },
    applyHtmlSource() {
      if (!this.showHtmlSource) return
      this.editor.commands.setContent(this.htmlSource, false)
      this.$emit('update:modelValue', this.editor.getHTML())
    },

    setBadgeBorderColor(color) {
      this.badgeBorderColor = color
      this.editor.chain().focus().updateAttributes('highlightBadge', { borderColor: color }).run()
    },
    setBadgeTextColor(color) {
      this.badgeTextColor = color
      this.editor.chain().focus().updateAttributes('highlightBadge', { textColor: color }).run()
    },
    setBadgeBackgroundColor(color) {
      this.badgeBackgroundColor = color
      this.editor.chain().focus().updateAttributes('highlightBadge', { backgroundColor: color }).run()
    },

    setIconListColor(color) {
      this.iconListColor = color
      this.editor.chain().focus().updateAttributes('iconList', { iconColor: color }).run()
    },

    // Same open/close pattern as the icon picker in IconListItemNodeView: a capture-phase
    // document listener closes the menu on outside clicks and is always removed on close.
    toggleBlocksMenu() {
      if (this.showBlocksMenu) {
        this.closeBlocksMenu()
        return
      }
      this.showBlocksMenu = true
      this.$nextTick(() => document.addEventListener('click', this.handleBlocksMenuOutsideClick, true))
    },
    handleBlocksMenuOutsideClick(event) {
      if (this.$refs.blocksMenu && !this.$refs.blocksMenu.contains(event.target)) {
        this.closeBlocksMenu()
      }
    },
    closeBlocksMenu({ focusTrigger = false } = {}) {
      this.showBlocksMenu = false
      document.removeEventListener('click', this.handleBlocksMenuOutsideClick, true)
      if (focusTrigger && this.$refs.blocksMenuTrigger) {
        this.$refs.blocksMenuTrigger.focus()
      }
    },
    insertBlock(entry) {
      this.closeBlocksMenu()
      const attrs = entry.attrs ? entry.attrs(this.brandHex, key => this.$t(key)) : undefined
      this.editor.chain().focus()[entry.command](attrs).run()
    },
  },
}
</script>

<style>
.tiptap-toolbar .toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.85rem;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  transition: background-color 0.15s;
}

.tiptap-toolbar .toolbar-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.tiptap-toolbar .toolbar-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.tiptap-toolbar .toolbar-btn-danger {
  color: #b45309;
  border-color: #fcd34d;
  background-color: #fffbeb;
}

.tiptap-toolbar .toolbar-btn-danger:hover:not(:disabled) {
  background-color: #fef3c7;
}

.tiptap-toolbar .toolbar-divider {
  display: inline-block;
  width: 1px;
  height: 1.5rem;
  background-color: #d1d5db;
  margin: 0 0.25rem;
  align-self: center;
}

.tiptap-html-source {
  background-color: #f9fafb;
  color: #374151;
  border-top: 1px solid #e5e7eb;
  display: block;
}

.tiptap-content .ProseMirror {
  outline: none;
}

.tiptap-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}
</style>
